import React, { useState } from "react";
import {
  Box, Flex, Text, Button, Icon, Image,
  useColorMode, useDisclosure, useMediaQuery,
  Drawer, DrawerBody, DrawerHeader, DrawerOverlay, 
  DrawerContent, DrawerCloseButton, useColorModeValue
} from "@chakra-ui/react";
import { FaMoon, FaSun, FaBars } from "react-icons/fa";
import { Link as RouterLink, useLocation } from "react-router-dom";
import useFeedStore from "../../stores/useFeedStore";

const Header = () => {
  const { colorMode, toggleColorMode } = useColorMode();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [isMobile] = useMediaQuery("(max-width: 768px)");
  const location = useLocation();
  const { categories } = useFeedStore();
  
  // Color settings
  const bgColor = useColorModeValue("white", "gray.800");
  const textColor = useColorModeValue("gray.700", "gray.200");
  const activeColor = useColorModeValue("blue.500", "blue.300");
  const logoColor = useColorModeValue("blue.500", "blue.300");
  const themeIconColor = useColorModeValue("gray.700", "yellow.300");

  // Main categories - keep Chinese for database queries but display in English
  const categoryMapping = {
    'AI行业动态': 'AI Industry',
    'Web3动态': 'Crypto',
    '企业家动态': 'Entrepreneur',
    '投资人动态': 'Investor',
    '一人公司动态': 'Solo Company',
    '科技': 'Technology'
  };

  const mainCategories = ['AI行业动态', 'Web3动态', '企业家动态', '投资人动态', '一人公司动态'].filter(
    cat => categories.includes(cat)
  );

  // Determine current active link
  const isActive = (path) => location.pathname === path;
  const isCategoryActive = (category) => location.pathname === `/category/${encodeURIComponent(category)}`;
  
  return (
    <Box as="header" bg={bgColor} boxShadow="sm" position="sticky" top="0" zIndex="999">
      <Flex 
        maxW="1200px" 
        mx="auto" 
        px={4} 
        py={3} 
        align="center"
      >
        {/* Website Logo */}
        <RouterLink to="/">
          <Flex align="center">
            <Box bg={logoColor} color="white" px={3} py={1} borderRadius="md" fontWeight="bold">
              InfoFlow
            </Box>
          </Flex>
        </RouterLink>

        {/* Desktop Navigation Links */}
        <Flex display={{ base: "none", md: "flex" }} ml={8}>
          <Text 
            as={RouterLink} 
            to="/" 
            mx={4} 
            fontWeight={isActive('/') ? "bold" : "normal"}
            color={isActive('/') ? activeColor : textColor}
          >
            Home
          </Text>
          
          {mainCategories.map(category => (
            <Text
              key={category}
              as={RouterLink}
              to={`/category/${encodeURIComponent(category)}`}
              mx={4}
              fontWeight={isCategoryActive(category) ? "bold" : "normal"}
              color={isCategoryActive(category) ? activeColor : textColor}
            >
              {categoryMapping[category] || category}
            </Text>
          ))}
          
          <Text 
            as={RouterLink} 
            to="/debug/edge-function" 
            mx={4}
            fontWeight={isActive('/debug/edge-function') ? "bold" : "normal"}
            color={isActive('/debug/edge-function') ? activeColor : textColor}
          >
            Admin
          </Text>

          <Text 
            as={RouterLink} 
            to="/about" 
            mx={4}
            fontWeight={isActive('/about') ? "bold" : "normal"}
            color={isActive('/about') ? activeColor : textColor}
          >
            ReachOut
          </Text>
        </Flex>
        
        <Box ml="auto" display="flex" alignItems="center">
          {/* Color mode toggle */}
          <Button 
            onClick={toggleColorMode} 
            variant="ghost" 
            mr={2} 
            size="sm"
            color={themeIconColor}
            _hover={{
              bg: useColorModeValue("gray.100", "gray.700")
            }}
          >
            <Icon as={colorMode === "light" ? FaMoon : FaSun} />
          </Button>
          
          {/* Mobile menu button */}
          <Button
            display={{ base: "flex", md: "none" }}
            variant="ghost"
            onClick={onOpen}
            size="sm"
            color={textColor}
            _hover={{
              bg: useColorModeValue("gray.100", "gray.700")
            }}
          >
            <Icon as={FaBars} />
          </Button>
        </Box>
      </Flex>
      
      {/* Mobile drawer menu */}
      <Drawer isOpen={isOpen} placement="right" onClose={onClose}>
        <DrawerOverlay />
        <DrawerContent bg={useColorModeValue("white", "gray.800")}>
          <DrawerCloseButton />
          <DrawerHeader color={textColor}>Menu</DrawerHeader>
          <DrawerBody>
            <Flex direction="column" gap={4}>
              <Text 
                as={RouterLink} 
                to="/" 
                fontWeight={isActive('/') ? "bold" : "normal"}
                color={isActive('/') ? activeColor : textColor}
                onClick={onClose}
              >
                Home
              </Text>
              
              {mainCategories.map(category => (
                <Text
                  key={category}
                  as={RouterLink}
                  to={`/category/${encodeURIComponent(category)}`}
                  fontWeight={isCategoryActive(category) ? "bold" : "normal"}
                  color={isCategoryActive(category) ? activeColor : textColor}
                  onClick={onClose}
                >
                  {categoryMapping[category] || category}
                </Text>
              ))}
              
              <Text 
                as={RouterLink} 
                to="/debug/edge-function" 
                fontWeight={isActive('/debug/edge-function') ? "bold" : "normal"}
                color={isActive('/debug/edge-function') ? activeColor : textColor}
                onClick={onClose}
              >
                Admin
              </Text>
              
              <Text 
                as={RouterLink} 
                to="/about" 
                fontWeight={isActive('/about') ? "bold" : "normal"}
                color={isActive('/about') ? activeColor : textColor}
                onClick={onClose}
              >
                ReachOut
              </Text>
            </Flex>
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </Box>
  );
};

export default Header;
